import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, BarChart3, DollarSign, TrendingUp } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import DashboardLayout from "../components/dashboard/DashboardLayout";
import { Card } from "../components/ui/Card";
import { useTrading } from "../context/TradingContext";

const formatMoney = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(value || 0);

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading } = useAuth();
  const { account, portfolio, transactions, notifications } = useTrading();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate("/login", { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  if (isLoading) {
    return null;
  }

  const statCards = [
    {
      title: "Total Portfolio Value",
      value: formatMoney(portfolio.totalValue),
      subtitle: "Updates live with the shared trading balance",
      icon: DollarSign,
    },
    {
      title: "Total P&L",
      value: formatMoney(portfolio.totalPnL),
      subtitle: `${portfolio.totalPnLPercent.toFixed(2)}% realized return`,
      icon: TrendingUp,
    },
    {
      title: "Available Balance",
      value: formatMoney(portfolio.availableBalance),
      subtitle: "Synced live across dashboard, wallet, and Trade Live",
      icon: Activity,
    },
    {
      title: "Total Invested",
      value: formatMoney(account.totalInvested),
      subtitle: "Live capital currently committed to trading",
      icon: BarChart3,
    },
  ];

  const recentActivity = [...transactions, ...notifications]
    .sort(
      (left, right) =>
        new Date(right.timestamp || 0).getTime() - new Date(left.timestamp || 0).getTime()
    )
    .slice(0, 6);

  return (
    <DashboardLayout>
      <div className="space-y-5 p-4 sm:space-y-6 sm:p-6 lg:p-8">
        <div className="overflow-hidden rounded-[1.75rem] border border-slate-800 bg-[radial-gradient(circle_at_top_left,_rgba(34,211,238,0.22),_transparent_26%),linear-gradient(135deg,#020617,#0f172a_52%,#082f49)] p-5 text-white shadow-2xl shadow-cyan-950/20 sm:rounded-[2rem] sm:p-7">
          <div className="grid gap-6 xl:grid-cols-[1.35fr_0.65fr] xl:items-end">
            <div>
              <div className="inline-flex rounded-full border border-cyan-400/30 bg-cyan-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-cyan-200">
                Live Account Center
              </div>
              <h1 className="mt-4 text-3xl font-semibold tracking-tight xl:text-4xl">{`WELCOME ${String(
                user?.name || "TRADER"
              ).toUpperCase()}`}</h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-300">
                Real-time balances, realized performance, and account activity remain synced across
                your dashboard, wallet, and trading pages.
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-400">Live Portfolio</div>
                <div className="mt-2 text-2xl font-semibold text-white">{formatMoney(portfolio.totalValue)}</div>
                <div className="mt-1 text-xs text-slate-400">Updated from shared trading state</div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-400">Open Capital</div>
                <div className="mt-2 text-2xl font-semibold text-cyan-300">{formatMoney(account.totalInvested)}</div>
                <div className="mt-1 text-xs text-slate-400">Capital currently committed to trades</div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-4 sm:gap-5 md:grid-cols-2 xl:grid-cols-4">
          {statCards.map((card) => {
            const Icon = card.icon;
            return (
              <Card key={card.title} className="border-slate-200/80 bg-white/90 shadow-sm dark:border-slate-800 dark:bg-slate-950/80">
                <div className="flex items-start justify-between gap-4">
                  <div className="w-full">
                    <div className="text-xs font-medium uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">{card.title}</div>
                    <div className="mt-3 text-3xl font-semibold tracking-tight text-slate-950 dark:text-white">{card.value}</div>
                    <div className="mt-3 text-sm text-slate-500 dark:text-slate-500">{card.subtitle}</div>
                  </div>
                  <div className="rounded-2xl bg-cyan-500/10 p-3 text-cyan-500 shadow-inner shadow-cyan-500/10">
                    <Icon className="h-5 w-5" />
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        <div className="grid gap-4 sm:gap-6 xl:grid-cols-[1.1fr_0.9fr]">
          <Card className="border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950/80">
            <h2 className="text-lg font-semibold text-slate-950 dark:text-white">Activity Summary</h2>
            <div className="mt-5 grid gap-4 lg:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 dark:border-slate-800 dark:bg-slate-900/70">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Pending transactions</div>
              <div className="mt-2 text-2xl font-semibold text-amber-500">
                {transactions.filter((transaction) => transaction.status === "pending").length}
              </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 dark:border-slate-800 dark:bg-slate-900/70">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Completed transactions</div>
              <div className="mt-2 text-2xl font-semibold text-emerald-500">
                {transactions.filter((transaction) => transaction.status === "completed").length}
              </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 dark:border-slate-800 dark:bg-slate-900/70">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Winning trades</div>
              <div className="mt-2 text-2xl font-semibold text-slate-950 dark:text-white">
                {portfolio.winningTrades}
              </div>
              </div>
            </div>
          </Card>

          <Card className="border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950/80">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-950 dark:text-white">Live Actions</h2>
              <div className="rounded-full border border-cyan-500/20 bg-cyan-500/10 px-3 py-1 text-xs text-cyan-500">
                Real Time Feed
              </div>
            </div>
            <div className="space-y-3">
              {recentActivity.length ? (
                recentActivity.map((entry) => (
                  <div
                    key={`${entry.id}-${entry.timestamp}`}
                    className="rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3 dark:border-slate-800 dark:bg-slate-900/70"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="text-sm text-slate-700 dark:text-slate-200">
                        {entry.title || entry.symbol || entry.message || entry.side || "Account update"}
                      </div>
                      <div className="shrink-0 text-xs text-slate-500">
                        {new Date(entry.timestamp).toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-8 text-sm text-slate-500 dark:border-slate-700">
                  No live actions yet.
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
